import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { customizeValidator } from "@rjsf/validator-ajv8";
import JSONSchemaForm from "@rjsf/bootstrap-4";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import customFormats from "@beeldit/core/custom-validations/custom-validation";
import useProcessSchema from "@beeldit/core/hooks/useProcessSchema";
import widgets from "@beeldit/core/widgets/widgets";
import BeelditTypeSelector from "@beeldit/core/components/BeelditTypeSelector";
import CategorySelector from "@beeldit/ecommerce/categories/components/CategorySelector";
import { ConfigurationsContext } from "@main/contexts/ConfigurationsContext";
import { ButtonGroup, ToggleButton } from "react-bootstrap";
import MultidimensionalCustomFormTemplate from "@beeldit/core/templates/MultidimensionalCustomFormTemplate";
import index from "@beeldit/core/services";
import checkRoles from "@beeldit/user-and-access/auth/services/checkRoles";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import SchemaHelper from "@bloonde/cms/helpers/SchemaHelper";
import RowArrayFieldTemplate from "@beeldit/core/templates/RowArrayFieldTemplate";
import GalleryArrayFieldTemplate from "@main/templates/GalleryArrayFieldTemplate";
import GalleryArrayFieldItemTemplate from "@main/templates/GalleryArrayFieldItemTemplate";

interface Prop {
  formConfig: {
    formRef: any;
    onSubmit: any;
    context: any;
    className?: string;
    producerSelectorComponent: any;
  };
}

interface Context {
  element: any;
  setElement: (element: any) => any;
  selectedLang: string;
  setSelectedLang: (lang: string) => any;
  backendFormErrors: any;
  attributes: any[];
  setAttributes: (attributes: any) => void;
}

function ProductForm(props: Prop) {
  const { formRef, onSubmit, context, className, producerSelectorComponent } =
    props.formConfig;
  const {
    element,
    setElement,
    backendFormErrors,
    selectedLang,
    setSelectedLang,
    attributes,
    setAttributes,
  } = useContext<Context>(context);

  const { langs } = useContext<any>(ConfigurationsContext);
  const { user } = useContext(AuthContext);

  const { t } = useTranslation();

  const [finalSchema, setFinalSchema] = useState<RJSFSchema>({});
  const [finalUiSchema, setFinalUiSchema] = useState<UiSchema>({});

  const translations = {
    category_id: t("category_id"),
    // brand: t('brand'),
    name: t("name"),
    name_lang_code: t("name_lang_code"),
    ref: t("ref"),
    description: t("description"),
    attributes: t("attributes"),
    producer_id: t("producer_id"),
    gallery: t("gallery"),
    image_alt: t("image_alt"),
    img: t("img"),
    main: t("main"),
    published: t("published"),
  };

  let schema: any = {
    type: "object",
    properties: {
      category_id: {
        type: "integer",
        title: translations.category_id,
      },
      producer_id: {
        type: ["integer", "null"],
        title: translations.producer_id,
      },
      ref: {
        type: "string",
        title: translations.ref,
      },
      // brand: {
      //   title: "",
      //   type: "object",
      //   properties: {},
      // },
      name: {
        title: "",
        type: "object",
        properties: {
          es: {
            type: "string",
            title: translations.name,
          },
          en: {
            type: "string",
            title: translations.name,
          },
          pt: {
            type: "string",
            title: translations.name,
          },
        },
      },
      description: {
        title: "",
        type: "object",
        properties: {
          es: {
            type: ["string", "null"],
            title: translations.description,
          },
          en: {
            type: ["string", "null"],
            title: translations.description,
          },
          pt: {
            type: ["string", "null"],
            title: translations.description,
          },
        },
      },
      attributes: {
        title: translations.attributes,
        type: "object",
        properties: {},
      },
      published: {
        type: ["boolean", "number"],
        title: translations.published,
      },
      gallery: {
        title: translations.gallery,
        type: "array",
        items: {
          type: "object",
          title: "",
          properties: {
            img: {
              type: "array",
              title: "",
              items: {
                type: "object",
                properties: {
                  filename: {
                    type: "string",
                  },
                  url: {
                    type: "string",
                  },
                },
              },
            },
            alt: {
              type: ["object", "null"],
              properties: {
                es: {
                  type: ["string", "null"],
                  title: translations.image_alt,
                },
                en: {
                  type: ["string", "null"],
                  title: translations.image_alt,
                },
                pt: {
                  type: ["string", "null"],
                  title: translations.image_alt,
                },
              },
              title: "",
            },
            main: {
              type: "boolean",
              title: translations.main,
            },
          },
        },
      },
    },
  };
  const uiSchema: UiSchema = {
    "ui:classNames": className,
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    category_id: {
      "ui:widget": "category-selector",
    },
    producer_id: {
      "ui:widget": checkRoles(user, ["admin"]) ? "producer-selector" : "hidden",
    },
    name: {},
    // brand: {},
    description: {
      en: {
        "ui:widget": "textarea",
      },
      es: {
        "ui:widget": "textarea",
      },
      pt: {
        "ui:widget": "textarea",
      },
    },
    attributes: {},
    published: {
      "ui:widget": "switch",
    },
    gallery: {
      "ui:ArrayFieldTemplate": GalleryArrayFieldTemplate,
      "ui:ArrayFieldItemTemplate": GalleryArrayFieldItemTemplate,
      cols: "col-4",
      items: {
        img: {
          "ui:widget": "multiple-file-input",
          "ui:options": {
            label: false,
          },
        },
        alt: {},
        main: {
          "ui:widget": "radio-button",
          "ui:options": {
            "radio-name": "main",
            label: false,
          },
        },
      },
    },
    colsSchema: [
      // Parte izquierda con 4 columnas con customer y pet
      {
        cols: "col-6",
        elements: [
          [
            { name: "category_id", cols: "col-12" },
            { name: "producer_id", cols: "col-12" },
            { name: "ref", cols: "col-6" },
            { name: "name", cols: "col-6" },
            // { name: "brand", cols: "col-6" },
            { name: "description", cols: "col-12" },
            { name: "published", cols: "col-12" },
          ],
        ],
      },
      {
        cols: "col-6",
        elements: [[{ name: "attributes", cols: "col-12" }]],
      },
      {
        cols: "col-12",
        elements: [[{ name: "gallery", cols: "col-12" }]],
      },
    ],
  };
  const customWidgets = {
    ...widgets,
    ...{ "category-selector": CategorySelector },
    ...{ "producer-selector": producerSelectorComponent },
    ...{ "type-selector": BeelditTypeSelector },
  };
  const handleChange = (type: any) => {
    /** Este método de momento es necesario aunque en realidad no tenga sentido, pero se debe a un bug de la libreria */
    // Actualizar el estado solo si no hay errores de validación
    setElement(type.formData);
    return console.log.bind(console, type);
  };
  const log = (type: any) => {
    return console.log.bind(console, type);
  };

  const getAttributes = () => {
    index("attributes", "relations=options&combination=0").then(
      (response: any) => {
        setAttributes(response.data.elements);
      }
    );
  };

  useEffect(() => {
    getAttributes();
  }, []);

  const formats = customFormats;
  const validator = customizeValidator({ customFormats: formats });

  const processSchemaFunction = useProcessSchema();

  const getSchemaByLang = () => {
    const langSchema = SchemaHelper.filterByLanguage(schema, selectedLang);
    const langUiSchema = uiSchema;
    return { langSchema, langUiSchema };
  };

  const setAttributeFields = (langSchema: any, langUiSchema: any) => {
    let new_attributes: any = {};

    langUiSchema["attributes"]["colsSchema"] = [
      {
        cols: "col-12",
        elements: [[]],
      },
    ];

    for (let attribute of attributes) {
      // Solo incluir atributos que tengan opciones
      if (!attribute.options || attribute.options.length === 0) {
        continue;
      }

      new_attributes["attribute_" + attribute.id] = {
        type: "array",
        title: attribute.name,
        items: {
          type: "string",
          anyOf: attribute.options.map((option: any) => ({
            title: t(option.name_lang_code),
            type: "string",
            enum: [option.name_lang_code],
          })),
          // enum: attribute.options.map((option: any) => option.name_lang_code)
        },
        uniqueItems: true,
      };
      langUiSchema["attributes"]["attribute_" + attribute.id] = {
        "ui:widget": "checkboxes",
      };
      langUiSchema["attributes"]["colsSchema"][0]["elements"][0].push({
        name: "attribute_" + attribute.id,
        cols: "col-6",
      });
    }

    langSchema.properties.attributes.properties = new_attributes;

    return {
      langSchemaWithAttributes: langSchema,
      langUiSchemaWithAttributes: langUiSchema,
    };
  };

  useEffect(() => {
    if (selectedLang && element) {
      const { langSchema, langUiSchema } = getSchemaByLang();
      const { langSchemaWithAttributes, langUiSchemaWithAttributes } =
        setAttributeFields(langSchema, langUiSchema);
      setFinalSchema(processSchemaFunction(langSchemaWithAttributes, element));
      setFinalUiSchema(langUiSchemaWithAttributes);
      if (element.lang !== selectedLang) {
        const mutedElem = { ...element, lang: selectedLang };
        setElement(mutedElem);
      }
    }
  }, [langs, element, selectedLang]);

  const formTemplate = MultidimensionalCustomFormTemplate;
  const arrayTemplate = RowArrayFieldTemplate;

  return (
    <div className="position-relative">
      <div className="lang-selector">
        {
          <ButtonGroup>
            {langs.map((lang: string, index: number) => (
              <ToggleButton
                key={index}
                id={`product-${index}`}
                type="radio"
                variant="outline-primary"
                name="radio"
                value={lang}
                checked={selectedLang === lang}
                onChange={(e) => setSelectedLang(e.currentTarget.value)}
              >
                {t(lang)}
              </ToggleButton>
            ))}
          </ButtonGroup>
        }
      </div>

      <JSONSchemaForm
        noHtml5Validate
        showErrorList={false}
        ref={formRef}
        templates={{
          ObjectFieldTemplate: formTemplate,
          ArrayFieldTemplate: arrayTemplate,
        }}
        schema={finalSchema}
        uiSchema={finalUiSchema}
        widgets={customWidgets}
        formData={element}
        validator={validator}
        onChange={handleChange}
        onSubmit={onSubmit}
        onError={log("errors")}
        extraErrors={backendFormErrors}
      />
    </div>
  );
}

export default ProductForm;
